import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/api/client";

/**
 * Polls for "new picks today" and "new settled results since last check" and
 * surfaces them as one-tap toasts. Persists last-seen state in localStorage so
 * we don't re-fire on every tab change.
 *
 * - new-picks toast → when /predictions/today picks count grows vs last seen
 * - settled-results toast → when /results/yesterday settled count grows
 */
const POLL_MS = 90_000; // 90s — light load on the API
const LS_PICKS = "kl_last_picks_sig";
const LS_RESULTS = "kl_last_settled_sig";

function pickSig(payload) {
    const tiers = payload?.tiers || {};
    const total = (tiers["2"]?.length || 0)
        + (tiers["5"]?.length || 0)
        + (tiers["10"]?.length || 0);
    return `${payload?.date || ""}:${total}`;
}

function settledSig(payload) {
    const s = payload?.summary || {};
    return `${payload?.date || ""}:${s.won || 0}:${s.lost || 0}:${s.void || 0}`;
}

export default function useNotifications(enabled = true) {
    const seenRef = useRef({
        picks: typeof window !== "undefined" ? localStorage.getItem(LS_PICKS) : null,
        results: typeof window !== "undefined" ? localStorage.getItem(LS_RESULTS) : null,
    });

    useEffect(() => {
        if (!enabled || typeof window === "undefined") return undefined;

        let cancelled = false;

        const tick = async () => {
            try {
                const [today, yest] = await Promise.all([
                    api.get("/predictions/today").then((r) => r.data).catch(() => null),
                    api.get("/results/yesterday").then((r) => r.data).catch(() => null),
                ]);
                if (cancelled) return;

                if (today) {
                    const sig = pickSig(today);
                    const last = seenRef.current.picks;
                    if (last && sig !== last && sig.split(":")[1] !== "0") {
                        const total = sig.split(":")[1];
                        toast.success(`🎯 New picks just dropped — ${total} predictions live`, {
                            description: "Tap to view today's slip",
                            duration: 6000,
                            action: {
                                label: "View",
                                onClick: () => { window.location.href = "/dashboard"; },
                            },
                        });
                    }
                    seenRef.current.picks = sig;
                    localStorage.setItem(LS_PICKS, sig);
                }

                if (yest) {
                    const sig = settledSig(yest);
                    const last = seenRef.current.results;
                    if (last && sig !== last) {
                        const s = yest.summary || {};
                        const fresh = (s.won || 0) + (s.lost || 0) + (s.void || 0);
                        if (fresh > 0) {
                            toast(`📊 Yesterday's results updated — ${s.won}W / ${s.lost}L`, {
                                description: "See the settled slip",
                                duration: 6000,
                                action: {
                                    label: "Results",
                                    onClick: () => { window.location.href = "/results"; },
                                },
                            });
                        }
                    }
                    seenRef.current.results = sig;
                    localStorage.setItem(LS_RESULTS, sig);
                }
            } catch {
                // Silent — polling is best-effort.
            }
        };

        // Fire once after a small delay (so we don't compete with first paint)
        const initial = setTimeout(tick, 4000);
        const interval = setInterval(tick, POLL_MS);
        return () => {
            cancelled = true;
            clearTimeout(initial);
            clearInterval(interval);
        };
    }, [enabled]);
}
